import type { ApiClient } from "@carepass/api-client";
import type { Schemas } from "../workspace/api.generated";
import type { MemberSearchFormValues } from "./schema";

export interface MemberListQuery {
  readonly from?: string;
  readonly to?: string;
  readonly channelCode?: string;
  readonly planCode?: string;
  readonly keyword: string;
  readonly status: MemberSearchFormValues["status"];
  readonly page: number;
  readonly size: number;
}

export interface MemberPaymentQuery {
  readonly page: number;
  readonly size: number;
}

export async function getMembers(
  apiClient: ApiClient,
  query: MemberListQuery,
  signal?: AbortSignal,
): Promise<Schemas["MemberSearchPage"]> {
  const response = await apiClient.get<Schemas["MemberSearchPage"]>(
    "/api/v1/admin/members",
    {
      query: {
        keyword: query.keyword || undefined,
        from: query.from || undefined,
        to: query.to || undefined,
        channelCode: query.channelCode || undefined,
        planCode: query.planCode || undefined,
        status: query.status || undefined,
        page: query.page,
        size: query.size,
      },
      signal,
    },
  );
  return response.data;
}

export async function getMemberDetail(
  apiClient: ApiClient,
  memberId: number,
  signal?: AbortSignal,
): Promise<Schemas["MemberDetailView"]> {
  const response = await apiClient.get<Schemas["MemberDetailView"]>(
    `/api/v1/admin/members/${memberId}`,
    { signal },
  );
  return response.data;
}

export async function getMemberPayments(
  apiClient: ApiClient,
  memberId: number,
  query: MemberPaymentQuery,
  signal?: AbortSignal,
): Promise<Schemas["AdminPageSubscriptionPaymentView"]> {
  const response = await apiClient.get<Schemas["AdminPageSubscriptionPaymentView"]>(
    `/api/v1/admin/members/${memberId}/subscription-payments`,
    { query: { page: query.page, size: query.size }, signal },
  );
  return response.data;
}

export async function getMemberHistory(
  apiClient: ApiClient,
  memberId: number,
  signal?: AbortSignal,
): Promise<Schemas["MemberAdminHistoryView"]> {
  const response = await apiClient.get<Schemas["MemberAdminHistoryView"]>(
    `/api/v1/admin/bo/members/${memberId}/history`,
    { signal, cache: "no-store" },
  );
  return response.data;
}

export async function getMemberNotes(
  apiClient: ApiClient,
  memberId: number,
  signal?: AbortSignal,
): Promise<Schemas["MemberCareNoteView"][]> {
  const response = await apiClient.get<Schemas["MemberCareNoteView"][]>(
    `/api/v1/admin/members/${memberId}/care-notes`,
    { signal },
  );
  return response.data;
}

export async function createMemberNote(
  apiClient: ApiClient,
  memberId: number,
  input: Schemas["CreateMemberCareNoteRequest"],
  signal?: AbortSignal,
): Promise<Schemas["MemberCareNoteView"]> {
  const response = await apiClient.post<
    Schemas["MemberCareNoteView"],
    Schemas["CreateMemberCareNoteRequest"]
  >(`/api/v1/admin/members/${memberId}/care-notes`, input, { signal });
  return response.data;
}

export async function revealMemberData(
  apiClient: ApiClient,
  memberId: number,
  input: Schemas["UnmaskMemberRequest"],
  signal?: AbortSignal,
): Promise<Schemas["MemberSensitiveView"]> {
  const response = await apiClient.post<
    Schemas["MemberSensitiveView"],
    Schemas["UnmaskMemberRequest"]
  >(`/api/v1/admin/members/${memberId}/sensitive-data`, input, { signal });
  return response.data;
}

export async function getMemberTerminationPreview(
  apiClient: ApiClient,
  memberId: number,
  signal?: AbortSignal,
): Promise<Schemas["TerminationPreviewView"]> {
  const response = await apiClient.get<Schemas["TerminationPreviewView"]>(
    `/api/v1/admin/members/${memberId}/termination/preview`, { signal },
  );
  return response.data;
}

export async function requestMemberTermination(
  apiClient: ApiClient,
  memberId: number,
  input: Schemas["RequestTerminationRequest"],
  signal?: AbortSignal,
): Promise<Schemas["RefundView"]> {
  const response = await apiClient.post<
    Schemas["RefundView"],
    Schemas["RequestTerminationRequest"]
  >(`/api/v1/admin/members/${memberId}/termination`, input, { signal });
  return response.data;
}

export async function exportMembers(apiClient: ApiClient, query: MemberListQuery): Promise<Blob> {
  return (await apiClient.get<Blob>("/api/v1/admin/members/export.xlsx", {
    query: { keyword: query.keyword || undefined, status: query.status || undefined, from: query.from || undefined, to: query.to || undefined, channelCode: query.channelCode || undefined, planCode: query.planCode || undefined }, responseType: "blob", cache: "no-store",
  })).data;
}
